import {FixedSizeList as Lists} from 'react-window';
import {
  ArrowBack,
  ArrowForward,
  CheckBox,
  CheckBoxOutlineBlank,
} from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  ListItemButton,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {
  IListAllCompaniesQueryParams,
  useRqListAllCompaniesList,
} from '../../../../react_query/companies/CompanyWithoutPagination';
import {
  IState,
  useRqListStates,
} from '../../../../react_query/product-representatives/ListProdRepresentative';
import {useRqProductsLevelsList} from '../../manage-product/ProductQueries';
import InputSearch from '../../../../components/styles/InputSearch';
import ProductsMultipleSelect from '../ProductsMultipleSelect';
import {SelectDropdownRenderTags} from '../SelectDropdownRenderTags';
import {TnnPartnerAmountTotals} from './TnnPartnerAmountTable';
import {
  IPartnerCompany,
  TNN_PARTNER_AMOUNT_FIELDS,
  TnnPartnerProductKey,
  formatPartnerCurrency,
  getPartnerPrice,
  getPartnerShare,
  normalizePartnerCompany,
  sumPartnerAmount,
  sumPartnerShare,
  updatePartnerCompanyPrice,
} from './tnnPartnerUtils';

export const queryDefault: IListAllCompaniesQueryParams = {
  search: '',
  dma: [],
  state: [],
  cmc_product: [],
  product_type: [],
  secondary_product_type: [],
  condition_type: 'and',
  is_participation_enabled: true,
};

export const createTnnPartnerQuery = (
  backendProductValue: 'GameTime' | 'Neptune Radio',
): IListAllCompaniesQueryParams => ({
  ...queryDefault,
  cmc_product: [backendProductValue],
  module: 'tnn-campaign',
});

const icon = <CheckBoxOutlineBlank fontSize='small' />;
const checkedIcon = <CheckBox fontSize='small' />;

export const StateMultipleSelect = ({
  className,
  selectedValues,
  handleChange,
}: {
  className: string;
  selectedValues: string[];
  handleChange: (type: string, selectedIds: string[]) => void;
}) => {
  const {data, isFetching, isError} = useRqListStates();

  if (isFetching || isError || !data) return null;

  return (
    <Autocomplete
      className={className}
      multiple
      limitTags={1}
      options={data}
      autoHighlight
      disableCloseOnSelect
      value={data.filter(option =>
        selectedValues.includes(option.id.toString()),
      )}
      getOptionLabel={option => option.name}
      onChange={(_: React.SyntheticEvent<Element, Event>, value: IState[]) => {
        handleChange(
          'state',
          value.map(option => option.id.toString()),
        );
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Checkbox
            icon={icon}
            checkedIcon={checkedIcon}
            sx={{mr: 1}}
            checked={selectedValues.includes(option.id.toString())}
          />
          {option.name}
        </li>
      )}
      renderTags={(value, getTagProps) => (
        <SelectDropdownRenderTags
          value={value.map(option => option.name)}
          getTagProps={getTagProps}
        />
      )}
      renderInput={params => <TextField {...params} label='State' />}
    />
  );
};

type PartnerListPanelProps = {
  title: string;
  companies: IPartnerCompany[];
  checkedIds: number[];
  isLoading?: boolean;
  isSelectedPanel?: boolean;
  onToggle: (company: IPartnerCompany) => void;
  onSelectAll?: (checked: boolean) => void;
  onPriceChange?: (companyId: number, value: string) => void;
};

const isInactiveCompany = (company: IPartnerCompany) =>
  Boolean(company.deleted_at || company.suspended_at);

const getCompanyStatus = (company: IPartnerCompany) =>
  company.deleted_at ? 'Deactivated' : company.suspended_at ? 'Suspended' : '';

const PartnerListGrid = ({
  children,
  isSelectedPanel,
  header = false,
}: {
  children: React.ReactNode;
  isSelectedPanel: boolean;
  header?: boolean;
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: isSelectedPanel
        ? 'minmax(0, 1fr) 100px 100px'
        : 'minmax(0, 1fr)',
      gap: 1,
      alignItems: 'center',
      ...(header ? {px: 2, pt: 1.5, pb: 1} : {width: '100%', minWidth: 0}),
    }}>
    {children}
  </Box>
);

type PartnerListRowData = {
  companies: IPartnerCompany[];
  checkedIds: number[];
  isSelectedPanel: boolean;
  onToggle: (company: IPartnerCompany) => void;
  onPriceChange?: (companyId: number, value: string) => void;
};

type PartnerListRowProps = {
  index: number;
  style: React.CSSProperties;
  data: PartnerListRowData;
};

const PartnerListRow = memo(({index, style, data}: PartnerListRowProps) => {
  const company = data.companies[index];
  if (!company) return null;

  const checked = data.checkedIds.includes(company.id);
  const inactive = isInactiveCompany(company);
  const status = getCompanyStatus(company);

  return (
    <Box style={{...style, padding: '0 8px'}}>
      <ListItemButton
        dense
        disabled={!data.isSelectedPanel && inactive}
        onClick={() => data.onToggle(company)}
        sx={{
          height: '100%',
          minHeight: 56,
          px: 1,
          borderBottom: '1px solid',
          borderColor: 'rgba(255,255,255,0.18)',
          borderRadius: 1,
          backgroundColor: checked ? 'rgba(0, 135, 196, 0.18)' : 'transparent',
          '&:hover': {
            backgroundColor: 'rgba(0, 135, 196, 0.2)',
          },
        }}>
        <Checkbox
          size='small'
          checked={checked}
          disabled={!data.isSelectedPanel && inactive}
          onChange={() => data.onToggle(company)}
          onClick={event => event.stopPropagation()}
          inputProps={{
            'aria-label': `${checked ? 'Deselect' : 'Select'} ${company.name}`,
          }}
        />
        <PartnerListGrid isSelectedPanel={data.isSelectedPanel}>
          <Typography
            noWrap
            title={company.name}
            sx={{
              fontSize: 14,
              color: inactive ? 'text.disabled' : 'text.primary',
            }}>
            {company.name}
            {status ? (
              <Typography
                component='span'
                sx={{ml: 1, fontSize: 11, color: 'warning.main'}}>
                ({status})
              </Typography>
            ) : null}
          </Typography>
          {data.isSelectedPanel ? (
            <TextField
              size='small'
              type='number'
              value={getPartnerPrice(company) ?? ''}
              onChange={event =>
                data.onPriceChange?.(company.id, event.target.value)
              }
              onClick={event => event.stopPropagation()}
              onKeyDown={event => {
                if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
                  return;
                }

                const currentValue = Number(
                  (event.target as HTMLInputElement).value || 0,
                );
                if (!Number.isFinite(currentValue)) return;

                event.preventDefault();
                event.stopPropagation();
                const increment = event.key === 'ArrowUp' ? 1 : -1;
                const nextValue = Math.max(0, currentValue + increment);
                data.onPriceChange?.(company.id, String(nextValue));
              }}
              inputProps={{
                min: 0,
                step: 'any',
                'aria-label': `Partner Price for ${company.name}`,
              }}
              sx={{
                width: 100,
                '& .MuiInputBase-root': {height: 36},
              }}
            />
          ) : null}
          {data.isSelectedPanel ? (
            <Typography sx={{fontSize: 14, whiteSpace: 'nowrap'}}>
              {formatPartnerCurrency(getPartnerShare(company))}
            </Typography>
          ) : null}
        </PartnerListGrid>
      </ListItemButton>
    </Box>
  );
});

PartnerListRow.displayName = 'PartnerListRow';

const PartnerListPanel = memo(
  ({
    title,
    companies,
    checkedIds,
    isLoading = false,
    isSelectedPanel = false,
    onToggle,
    onSelectAll,
    onPriceChange,
  }: PartnerListPanelProps) => {
    const selectableIds = useMemo(
      () =>
        companies
          .filter(company => isSelectedPanel || !isInactiveCompany(company))
          .map(company => company.id),
      [companies, isSelectedPanel],
    );
    const checkedSelectableCount = selectableIds.filter(id =>
      checkedIds.includes(id),
    ).length;
    const allSelectableChecked =
      selectableIds.length > 0 &&
      checkedSelectableCount === selectableIds.length;
    const isIndeterminate = checkedSelectableCount > 0 && !allSelectableChecked;
    const totalPrice = isSelectedPanel
      ? sumPartnerAmount(companies, TNN_PARTNER_AMOUNT_FIELDS.price)
      : null;
    const totalShare = isSelectedPanel ? sumPartnerShare(companies) : null;

    return (
      <Stack
        sx={{
          flex: isSelectedPanel ? '1 1 0' : '0 0 min(400px, 42%)',
          minWidth: 0,
          minHeight: 420,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3.5,
          backgroundColor: 'background.default',
          overflow: 'hidden',
        }}>
        <PartnerListGrid isSelectedPanel={isSelectedPanel} header>
          <Stack direction='row' alignItems='center' spacing={1} minWidth={0}>
            <Checkbox
              size='small'
              checked={allSelectableChecked}
              indeterminate={isIndeterminate}
              disabled={selectableIds.length === 0}
              onChange={event => onSelectAll?.(event.target.checked)}
              inputProps={{'aria-label': `Select all ${title}`}}
            />
            <Typography noWrap sx={{fontSize: 14, color: 'text.secondary'}}>
              {title}{' '}
              <Box component='span' sx={{color: 'primary.main'}}>
                ({companies.length})
              </Box>
            </Typography>
          </Stack>
          {isSelectedPanel ? (
            <>
              <Typography sx={{fontSize: 13, color: 'text.secondary'}}>
                Partner Price
              </Typography>
              <Typography sx={{fontSize: 13, color: 'text.secondary'}}>
                Partner Share
              </Typography>
            </>
          ) : null}
        </PartnerListGrid>
        <Divider sx={{borderColor: 'divider'}} />
        <Stack sx={{flex: 1, minHeight: 0, py: 1}}>
          {isLoading && companies.length === 0 ? (
            <Stack spacing={1} sx={{px: 2}}>
              {Array.from({length: 6}).map((_, index) => (
                <Skeleton key={index} variant='rounded' height={48} />
              ))}
            </Stack>
          ) : companies.length === 0 ? (
            <Stack
              alignItems='center'
              justifyContent='center'
              sx={{height: '100%', minHeight: 240, px: 2}}>
              <Typography sx={{color: 'text.secondary', fontSize: 14}}>
                {isLoading ? 'Loading partners…' : 'No partners found'}
              </Typography>
              {isLoading ? <CircularProgress size={22} sx={{mt: 1}} /> : null}
            </Stack>
          ) : (
            <Lists
              height={390}
              itemCount={companies.length}
              itemSize={64}
              itemKey={(index: number) => companies[index]?.id ?? index}
              width='100%'
              className='scrollbar'
              itemData={{
                companies,
                checkedIds,
                isSelectedPanel,
                onToggle,
                onPriceChange,
              }}>
              {PartnerListRow}
            </Lists>
          )}
        </Stack>
        {isSelectedPanel ? (
          <TnnPartnerAmountTotals
            price={totalPrice}
            share={totalShare}
            currencyMinimumFractionDigits={2}
          />
        ) : null}
      </Stack>
    );
  },
);

PartnerListPanel.displayName = 'PartnerListPanel';

export type TnnPartnerSelectionProps = {
  title: string;
  productKey: TnnPartnerProductKey;
  backendProductValue: 'GameTime' | 'Neptune Radio';
  selectedCompanies: IPartnerCompany[];
  onSelectedCompaniesChange: (companies: IPartnerCompany[]) => void;
  query: IListAllCompaniesQueryParams;
  setQuery: React.Dispatch<React.SetStateAction<IListAllCompaniesQueryParams>>;
  showTitle?: boolean;
};

export const TnnPartnerSelection = memo(
  ({
    title,
    productKey,
    backendProductValue,
    selectedCompanies,
    onSelectedCompaniesChange,
    query,
    setQuery,
    showTitle = false,
  }: TnnPartnerSelectionProps) => {
    const [availableCheckedIds, setAvailableCheckedIds] = useState<number[]>(
      [],
    );
    const [selectedCheckedIds, setSelectedCheckedIds] = useState<number[]>([]);
    const {
      data: productData,
      isFetching: productIsFetching,
      isError: productIsError,
    } = useRqProductsLevelsList(true);
    const {
      data: companies = [],
      isFetching,
      isError,
    } = useRqListAllCompaniesList(query);

    const selectedIds = useMemo(
      () => new Set(selectedCompanies.map(company => company.id)),
      [selectedCompanies],
    );
    const selectedCompaniesWithDetails = useMemo(() => {
      const companiesById = new Map(
        companies.map(company => [Number(company.id), company]),
      );

      return selectedCompanies.map(selectedCompany => {
        const company = companiesById.get(Number(selectedCompany.id));
        if (!company || selectedCompany.name) return selectedCompany;

        return {
          ...company,
          ...selectedCompany,
          name: company.name,
        };
      });
    }, [companies, selectedCompanies]);

    useEffect(() => {
      const hasEnrichedNames = selectedCompanies.some(
        (selectedCompany, index) =>
          selectedCompaniesWithDetails[index]?.name !== selectedCompany.name,
      );
      if (hasEnrichedNames) {
        onSelectedCompaniesChange(selectedCompaniesWithDetails);
      }
    }, [
      onSelectedCompaniesChange,
      selectedCompanies,
      selectedCompaniesWithDetails,
    ]);

    const availableCompanies = useMemo(
      () => companies.filter(company => !selectedIds.has(company.id)),
      [companies, selectedIds],
    );

    useEffect(() => {
      const availableIds = new Set(
        availableCompanies.map(company => company.id),
      );
      setAvailableCheckedIds(prev => {
        const next = prev.filter(id => availableIds.has(id));
        return next.length === prev.length &&
          next.every((id, index) => id === prev[index])
          ? prev
          : next;
      });
    }, [availableCompanies]);

    useEffect(() => {
      setSelectedCheckedIds(prev => {
        const next = prev.filter(id => selectedIds.has(id));
        return next.length === prev.length &&
          next.every((id, index) => id === prev[index])
          ? prev
          : next;
      });
    }, [selectedIds]);

    const [searchValue, setSearchValue] = useState(query.search || '');

    useEffect(() => {
      const timeoutId = window.setTimeout(() => {
        setQuery(prev =>
          prev.search === searchValue ? prev : {...prev, search: searchValue},
        );
      }, 300);

      return () => window.clearTimeout(timeoutId);
    }, [searchValue, setQuery]);

    const handleFilterChange = useCallback(
      (type: string, selectedIdsForFilter: string[]) => {
        const keyMap: Record<string, keyof IListAllCompaniesQueryParams> = {
          state: 'state',
          dma: 'dma',
          product: 'product_type',
          secondary: 'secondary_product_type',
        };
        const key = keyMap[type];
        if (key) {
          setQuery(prev => {
            const previousValues = prev[key];
            const valuesAreUnchanged =
              Array.isArray(previousValues) &&
              previousValues.length === selectedIdsForFilter.length &&
              previousValues.every(
                (value, index) => value === selectedIdsForFilter[index],
              );

            return valuesAreUnchanged
              ? prev
              : {...prev, [key]: selectedIdsForFilter};
          });
        }
      },
      [setQuery],
    );

    const handleSearch = useCallback((value: string) => {
      setSearchValue(value);
    }, []);

    const toggleChecked = useCallback(
      (
        setChecked: React.Dispatch<React.SetStateAction<number[]>>,
        company: IPartnerCompany,
      ) => {
        setChecked(prev =>
          prev.includes(company.id)
            ? prev.filter(id => id !== company.id)
            : [...prev, company.id],
        );
      },
      [],
    );

    const handleAvailableToggle = useCallback(
      (company: IPartnerCompany) =>
        toggleChecked(setAvailableCheckedIds, company),
      [toggleChecked],
    );

    const handleSelectedToggle = useCallback(
      (company: IPartnerCompany) =>
        toggleChecked(setSelectedCheckedIds, company),
      [toggleChecked],
    );

    const handleMoveToSelected = useCallback(() => {
      const checked = new Set(availableCheckedIds);
      const selected = availableCompanies
        .filter(
          company => checked.has(company.id) && !isInactiveCompany(company),
        )
        .map(normalizePartnerCompany);
      if (selected.length > 0) {
        onSelectedCompaniesChange([...selectedCompanies, ...selected]);
      }
      setAvailableCheckedIds([]);
    }, [
      availableCheckedIds,
      availableCompanies,
      onSelectedCompaniesChange,
      selectedCompanies,
    ]);

    const handleMoveToAvailable = useCallback(() => {
      const checked = new Set(selectedCheckedIds);
      onSelectedCompaniesChange(
        selectedCompanies.filter(company => !checked.has(company.id)),
      );
      setSelectedCheckedIds([]);
    }, [onSelectedCompaniesChange, selectedCompanies, selectedCheckedIds]);

    const handlePriceChange = useCallback(
      (companyId: number, rawValue: string) => {
        onSelectedCompaniesChange(
          selectedCompanies.map(company =>
            company.id === companyId
              ? updatePartnerCompanyPrice(company, rawValue)
              : company,
          ),
        );
      },
      [onSelectedCompaniesChange, selectedCompanies],
    );

    const handleSelectAllAvailable = useCallback(
      (checked: boolean) => {
        const selectableIds = availableCompanies
          .filter(company => !isInactiveCompany(company))
          .map(company => company.id);
        setAvailableCheckedIds(checked ? selectableIds : []);
      },
      [availableCompanies],
    );

    const handleSelectAllSelected = useCallback(
      (checked: boolean) => {
        setSelectedCheckedIds(
          checked
            ? selectedCompaniesWithDetails.map(company => company.id)
            : [],
        );
      },
      [selectedCompaniesWithDetails],
    );

    return (
      <Stack sx={{pt: 1, width: '100%', minWidth: 0}}>
        {showTitle ? (
          <Typography sx={{fontSize: 18, fontWeight: 600, mb: 2}}>
            {title}
          </Typography>
        ) : null}
        <Stack
          direction='row'
          spacing={1}
          alignItems='center'
          flexWrap='wrap'
          sx={{mb: 2}}>
          {!productIsFetching && !productIsError && productData ? (
            <>
              <StateMultipleSelect
                className='w-200'
                selectedValues={query.state}
                handleChange={handleFilterChange}
              />
              <ProductsMultipleSelect
                className='w-200'
                label='DMA'
                type='dma'
                data={productData.dma}
                handleChange={handleFilterChange}
                selecteValues={query.dma}
              />
              <ProductsMultipleSelect
                className='w-200'
                label='Primary Type'
                type='product'
                data={productData.primary}
                handleChange={handleFilterChange}
                selecteValues={query.product_type}
              />
              <ProductsMultipleSelect
                className='w-200'
                label='Secondary Type'
                type='secondary'
                data={productData.secondary}
                handleChange={handleFilterChange}
                selecteValues={query.secondary_product_type}
              />
            </>
          ) : null}
          <Stack sx={{ml: 'auto'}}>
            <InputSearch
              placeholder='Search partners'
              value={searchValue}
              onChange={handleSearch}
              className='w-320'
            />
          </Stack>
        </Stack>

        {isError ? (
          <Typography color='error' sx={{mb: 2}}>
            Unable to load partners.
          </Typography>
        ) : null}

        <Stack
          direction={{xs: 'column', lg: 'row'}}
          spacing={2}
          alignItems='stretch'
          sx={{minHeight: 420}}>
          <PartnerListPanel
            title='Available Partners'
            companies={availableCompanies}
            checkedIds={availableCheckedIds}
            isLoading={isFetching}
            onToggle={handleAvailableToggle}
            onSelectAll={handleSelectAllAvailable}
          />
          <Stack
            spacing={1.5}
            justifyContent='center'
            alignItems='center'
            sx={{flexDirection: {xs: 'row', lg: 'column'}}}>
            <IconButton
              color='primary'
              disabled={availableCheckedIds.length === 0}
              onClick={handleMoveToSelected}
              aria-label={`Add selected ${backendProductValue} partners`}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor:
                  availableCheckedIds.length > 0
                    ? 'rgba(0, 135, 196, 0.25)'
                    : 'transparent',
              }}>
              <ArrowForward />
            </IconButton>
            <IconButton
              color='primary'
              disabled={selectedCheckedIds.length === 0}
              onClick={handleMoveToAvailable}
              aria-label={`Remove selected ${backendProductValue} partners`}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor:
                  selectedCheckedIds.length > 0
                    ? 'rgba(0, 135, 196, 0.25)'
                    : 'transparent',
              }}>
              <ArrowBack />
            </IconButton>
          </Stack>
          <PartnerListPanel
            title='Selected Partners'
            companies={selectedCompaniesWithDetails}
            checkedIds={selectedCheckedIds}
            isSelectedPanel
            onToggle={handleSelectedToggle}
            onSelectAll={handleSelectAllSelected}
            onPriceChange={handlePriceChange}
          />
        </Stack>
        <Typography sx={{mt: 1, color: 'text.secondary', fontSize: 13}}>
          {selectedCompanies.length}{' '}
          {productKey === 'gametime' ? 'GameTime' : 'Neptune Radio'} partner
          {selectedCompanies.length === 1 ? '' : 's'} selected
        </Typography>
      </Stack>
    );
  },
);

TnnPartnerSelection.displayName = 'TnnPartnerSelection';
